import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { StatusLead } from '../../../types/leads';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { TextArea } = Input;

const StatusLeadList: React.FC = () => {
  const [statusLeadList, setStatusLeadList] = useState<StatusLead[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StatusLead | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadStatus.create');
  const canUpdate = hasPermission('leadStatus.update');
  const canDelete = hasPermission('leadStatus.delete');

  const fetchStatusLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-status');
      if (response.data && response.data.status === 'success') {
        setStatusLeadList(response.data.data || []);
      } else {
        setStatusLeadList([]);
        message.error('Gagal memuat data dengan format yang benar.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Gagal mengambil data status lead';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchStatusLeads();
  }, [fetchStatusLeads]);

  const showModal = (status: StatusLead | null = null) => {
    setEditingStatus(status);
    if (status) {
      form.setFieldsValue({
        name: status.name,
        description: status.description,
        is_default: status.is_default,
      });
    } else {
      // Set default for new entry
      form.setFieldsValue({
        name: '',
        description: '',
        is_default: false,
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingStatus(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Payload now includes is_default
      const payload = {
        name: values.name,
        description: values.description,
        is_default: values.is_default || false,
      };

      if (editingStatus) {
        await api.put(`lead-status/${editingStatus.id}`, payload);
        message.success('Status Lead berhasil diperbarui');
      } else {
        await api.post('lead-status', payload);
        message.success('Status Lead berhasil ditambahkan');
      }

      handleCancel();
      fetchStatusLeads();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: StatusLead) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `/lead-status/${record.id}`
        : `/lead-status/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Status Lead ${action} berhasil`);
      fetchStatusLeads();
    } catch (error: any) {
      const action = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Gagal ${action} status lead`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Status Lead',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (isDefault: boolean) =>
        isDefault ? <Tag color="gold">Default</Tag> : null,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: StatusLead) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Apakah Anda yakin ingin ${
                record.is_active ? 'deactivate' : 'reactivate'
              } status ini?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Ya"
              cancelText="Tidak"
              disabled={record.is_default}
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                disabled={record.is_default}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Status Lead Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Status Lead
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={statusLeadList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingStatus ? 'Edit Status Lead' : 'Tambah Status Lead Baru'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="statusLeadForm" initialValues={{ is_default: false }}>
          <Form.Item
            name="name"
            label="Nama Status Lead"
            rules={[{ required: true, message: 'Nama status tidak boleh kosong!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="is_default"
            label="Jadikan Default"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StatusLeadList;
